import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Table as AntTable, Button, Progress, Spin, Tag, Tooltip, Typography, notification,
} from 'antd';

import { CustomTable } from 'components';
import * as api from 'util/api.js';

import {
  CheckCircleOutlined, ExclamationCircleOutlined, LoadingOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import pluralize from 'pluralize';
import useCachedSkuSyncStatusQuery from './use-cached-sku-sync-status-query';

import './index.less';
import useStartManualSkuSyncMutation from './use-start-manual-sku-sync-mutation';
import SkuDiscrepanciesModal from './DiscrepanciesModal';
import { MONDAY_SKU_DATABASE_BOARD_ID } from '../constants';

const { Title, Paragraph } = Typography;
const { Column } = AntTable;

const REVIEWING_DISCREPANCIES_DEFAULT_VALUE = {
  active: false,
  skuName: null,
  matrixSkuId: null,
  mondaySkuId: null,
  parentMatrixProductId: null,
  parentProductName: null,
  discrepancies: null,
};

const DEFAULT_META_QUERY_INTERVAL = 60 * 1000 * 2; // 2 minutes
const STALE_SYNC_MS = 5 * 60 * 1000; // 5 minutes

const SkuSyncStatusTable = () => {
  /**
   * All product SKUs in the database (Matrix API — source of truth for rows
   * and progress denominator).
   */
  const [skus, setSkus] = useState(null);

  /**
   * Whether SKUs have been fetched.
   */
  const hasFetchedSkus = useMemo(() => Array.isArray(skus), [skus]);

  /**
   * Handles fetching SKUs and setting them in state.
   */
  const fetchSkus = useCallback(async () => {
    setSkus(await api.getAllProductSkus());
  }, []);

  /**
   * Fetch all SKUs from the database on mount.
   */
  useEffect(() => {
    fetchSkus();
  }, [fetchSkus]);

  /**
   * Tracks last time `meta.totalProcessed` changed (for stalled-sync detection).
   */
  const lastTotalProcessedRef = useRef(null);
  const lastProgressAtRef = useRef(Date.now());

  /**
   * True when sync has made no progress for STALE_SYNC_MS while server reports sync active.
   */
  const [syncStalled, setSyncStalled] = useState(false);

  /**
   * The interval to refetch the status of the SKU syncing, defaults
   * to 2 minutes until a sync is invoked, or is already being performed.
   */
  const [
    metaQueryRefetchInterval,
    setMetaQueryRefetchInterval,
  ] = useState(DEFAULT_META_QUERY_INTERVAL);

  /**
   * Fetch the current cached state for synced SKUs.
   */
  const cachedSkuSyncStatusQuery = useCachedSkuSyncStatusQuery({
    refetchInterval: metaQueryRefetchInterval,
  });

  /**
   * Deconstruct the query data.
   */
  const { data: cachedSkuSyncStatus } = cachedSkuSyncStatusQuery;

  /**
   * Whether a manual sync has been successfully requested - this allows the sync progress to be
   * displayed in the period between the request being made, and the meta being re-fetched.
   */
  const [successfullyRequestedSync, setSuccessfullyRequestedSync] = useState(false);

  /**
   * Reset the flag indicating that a sync has been requested if the flag has been set and
   * the meta data updates to reflect that the sync is active.
   */
  useEffect(() => {
    if (cachedSkuSyncStatusQuery.isSuccess) {
      if (cachedSkuSyncStatus?.meta?.syncActive && successfullyRequestedSync) {
        setSuccessfullyRequestedSync(false);
      }
    }
  }, [cachedSkuSyncStatusQuery, cachedSkuSyncStatus, successfullyRequestedSync]);

  /**
   * Whether SKUs are currently being manually synced (per server + optimistic request).
   */
  const isSyncing = useMemo(() => (
    successfullyRequestedSync || (
      cachedSkuSyncStatusQuery.isSuccess && cachedSkuSyncStatus?.meta?.syncActive
    )
  ), [successfullyRequestedSync, cachedSkuSyncStatusQuery, cachedSkuSyncStatus]);

  /**
   * When progress is stalled, treat as not "actively syncing" for UI so the user can retry.
   */
  const effectiveIsSyncing = useMemo(() => isSyncing && !syncStalled, [isSyncing, syncStalled]);

  /**
   * Clear stalled state when server reports sync finished.
   */
  useEffect(() => {
    if (cachedSkuSyncStatusQuery.isSuccess && !cachedSkuSyncStatus?.meta?.syncActive) {
      setSyncStalled(false);
    }
  }, [cachedSkuSyncStatusQuery.isSuccess, cachedSkuSyncStatus?.meta?.syncActive]);

  /**
   * Update `totalProcessed` change tracking whenever cached meta updates.
   */
  useEffect(() => {
    if (!cachedSkuSyncStatusQuery.isSuccess || !cachedSkuSyncStatus?.meta) return;
    const tp = cachedSkuSyncStatus.meta.totalProcessed;
    if (lastTotalProcessedRef.current !== tp) {
      lastTotalProcessedRef.current = tp;
      lastProgressAtRef.current = Date.now();
    }
  }, [cachedSkuSyncStatusQuery.isSuccess, cachedSkuSyncStatus?.meta?.totalProcessed]);

  /**
   * If `totalProcessed` does not change for STALE_SYNC_MS while sync is active, mark as stalled.
   */
  useEffect(() => {
    if (!isSyncing || syncStalled) return undefined;
    const id = setInterval(() => {
      if (!cachedSkuSyncStatus?.meta?.syncActive) return;
      if (Date.now() - lastProgressAtRef.current > STALE_SYNC_MS) {
        setSyncStalled(true);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [isSyncing, syncStalled, cachedSkuSyncStatus?.meta?.syncActive]);

  /**
   * Update the meta query sync interval when the sync is active and
   * it still set to the default interval, or revert to the default if
   * it is no longer active.
   */
  useEffect(() => {
    if (cachedSkuSyncStatusQuery.isSuccess) {
      if (isSyncing) {
        if (metaQueryRefetchInterval === DEFAULT_META_QUERY_INTERVAL) {
          setMetaQueryRefetchInterval(1000);
        }
      } else if (metaQueryRefetchInterval !== DEFAULT_META_QUERY_INTERVAL) {
        setMetaQueryRefetchInterval(DEFAULT_META_QUERY_INTERVAL);
      }
    }
  }, [cachedSkuSyncStatusQuery, isSyncing, metaQueryRefetchInterval]);

  /**
   * Whether the last sync was successful.
   */
  const lastSyncSuccess = useMemo(() => {
    if (cachedSkuSyncStatus?.meta?.syncActive) {
      return null;
    }
    return !cachedSkuSyncStatus?.meta?.syncError;
  }, [cachedSkuSyncStatus]);

  /**
   * Whether the data for the table is ready to be evaluated.
   */
  const dataIsReady = useMemo(() => (
    hasFetchedSkus && cachedSkuSyncStatusQuery.isSuccess
  ), [hasFetchedSkus, cachedSkuSyncStatusQuery]);

  /**
   * Calculate the sync percentage (Matrix SKU count as denominator — matches Product Sync Status).
   */
  const syncPercentage = useMemo(() => {
    if (!dataIsReady || !effectiveIsSyncing || successfullyRequestedSync) return 0;
    if (!skus.length) return 0;
    const { totalProcessed } = cachedSkuSyncStatus?.meta ?? {};
    return Number(((totalProcessed / skus.length) * 100).toFixed(2));
  }, [dataIsReady, effectiveIsSyncing, successfullyRequestedSync, cachedSkuSyncStatus, skus]);

  /**
   * Index cached statuses by matrixSkuId for O(1) lookup during merge.
   */
  const statusesBySkuId = useMemo(() => {
    if (!cachedSkuSyncStatus?.statuses) return new Map();
    return new Map(
      cachedSkuSyncStatus.statuses.map((s) => [Number(s.matrixSkuId), s]),
    );
  }, [cachedSkuSyncStatus]);

  /**
   * Merge Matrix SKUs with cached sync statuses (same pattern as ProductSyncStatusTable).
   */
  const tableData = useMemo(() => {
    if (!dataIsReady) return undefined;

    return skus.map((sku) => {
      const {
        discrepancies,
        lastChecked,
        lastSynced,
        skuName: cachedSkuName,
        parentProductName: cachedParentProductName,
        mondaySkuId: cachedMondaySkuId,
        parentMatrixProductId: cachedParentMatrixProductId,
      } = statusesBySkuId.get(Number(sku.key)) ?? {};

      return ({
        key: sku.key,
        skuName: cachedSkuName ?? sku.name ?? null,
        parentProductName: cachedParentProductName ?? null,
        matrixSkuId: sku.key,
        mondaySkuId: cachedMondaySkuId ?? sku.mondayId ?? null,
        parentMatrixProductId: cachedParentMatrixProductId ?? sku.productMatrixId ?? null,
        lastChecked: lastChecked ? dayjs(lastChecked).format('DD/MM/YYYY h:mma') : null,
        lastSynced: lastSynced ? dayjs(lastSynced).format('DD/MM/YYYY h:mma') : null,
        discrepancies: lastChecked && discrepancies ? discrepancies : null,
      });
    }).sort(
      ({ discrepancies: discrepanciesA }, { discrepancies: discrepanciesB }) => {
        if (discrepanciesA === null && discrepanciesB === null) {
          return 0;
        }
        if (discrepanciesA === null && discrepanciesB !== null) {
          return 1;
        }
        if (discrepanciesB === null && discrepanciesA !== null) {
          return -1;
        }
        return (
          discrepanciesB.length - discrepanciesA.length
        );
      },
    );
  }, [dataIsReady, skus, statusesBySkuId]);

  /**
   * The total number of SKUs that have discrepancies (from merged table rows).
   */
  const totalSkusWithDiscrepancies = useMemo(() => (
    !tableData ? 0 : tableData.filter(
      ({ discrepancies }) => Array.isArray(discrepancies) && discrepancies.length > 0,
    ).length
  ), [tableData]);

  /**
   * Access the query client.
   */
  const queryClient = useQueryClient();

  /**
   * Show a success notification when the manual sync has been successfully started.
   */
  const onStartManualSyncSuccess = useCallback(() => {
    lastTotalProcessedRef.current = null;
    lastProgressAtRef.current = Date.now();
    setSyncStalled(false);

    /**
     * Increase the frequency of the meta query to every second.
     */
    setMetaQueryRefetchInterval(1000);

    /**
     * Set the flag indicating that a sync has been requested.
     */
    setSuccessfullyRequestedSync(true);

    /**
     * Invalidate the query for the sync status.
     */
    queryClient.invalidateQueries('get_cached_sku_sync_status');

    /**
     * Show the notification.
     */
    notification.success({
      message: 'Successfully started manual SKU sync!',
    });
  }, [queryClient]);

  /**
   * Show an error notification when the manual sync failed to start.
   */
  const onStartManualSyncError = useCallback(() => {
    notification.error({
      message: 'There was an error starting the manual SKU sync. Please try again.',
    });
  }, []);

  /**
   * Mutation for invoking the manual SKU syncing.
   */
  const {
    isLoading: isStartingManualSkuSync,
    mutate: startManualSkuSync,
  } = useStartManualSkuSyncMutation({
    onSuccess: onStartManualSyncSuccess,
    onError: onStartManualSyncError,
  });

  /**
   * Discrepancies that are currently being reviewed.
   */
  const [reviewingDiscrepancies, setReviewingDiscrepancies] = useState(
    REVIEWING_DISCREPANCIES_DEFAULT_VALUE,
  );

  /**
   * Handle showing the modal to decide discrepancies for a SKU sync.
   */
  const onReviewDiscrepancies = useCallback(({
    matrixSkuId, mondaySkuId, parentMatrixProductId, parentProductName, skuName, discrepancies,
  }) => {
    setReviewingDiscrepancies({
      active: true,
      matrixSkuId,
      mondaySkuId,
      parentMatrixProductId,
      parentProductName,
      skuName,
      discrepancies,
    });
  }, []);

  const onCloseDiscrepanciesModal = useCallback((success) => {
    /**
     * Refetch SKUs and invalidate sync status if discrepancies were resolved.
     */
    if (success) {
      fetchSkus();
      queryClient.invalidateQueries('get_cached_sku_sync_status');
    }

    /**
     * Reset the modal state.
     */
    setReviewingDiscrepancies(REVIEWING_DISCREPANCIES_DEFAULT_VALUE);
  }, [fetchSkus, queryClient]);

  return (
    <>
      <SkuDiscrepanciesModal
        visible={reviewingDiscrepancies.active}
        onClose={onCloseDiscrepanciesModal}
        {...reviewingDiscrepancies}
      />
      <div id="monday-sku-sync-status-container">
        <div style={{ marginBottom: 10 }}>
          <Paragraph style={{ opacity: 0.7, marginBottom: 6 }}>
            The table below shows the sync status of the SKUs in
            the Matrix with Monday.
          </Paragraph>
          <Paragraph style={{ opacity: 0.7, marginBottom: 6 }}>
            A manual sync can be performed to ensure that all SKU data in the
            Matrix is up to date with the SKU items in Monday.
          </Paragraph>
          <Paragraph style={{ opacity: 0.7, marginBottom: 6 }}>
            When a sync is performed, both data in the Matrix and Monday
            may be updated. If there are discrepancies in data between what
            is in the Matrix and what is in Monday, you will need to review
            the data you would like to set as being correct.
          </Paragraph>
        </div>

        <div>
          <Title level={5}>Sync progress</Title>
          {!dataIsReady ? (
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />}
            />
          ) : (
            <>
              {isSyncing ? (
                <div style={{ maxWidth: '100%', marginRight: 20 }}>
                  {syncStalled ? (
                    <Tag
                      icon={<ExclamationCircleOutlined />}
                      color="red"
                      style={{
                        marginBottom: 8,
                        transform: 'scale(1.25)',
                        transformOrigin: 'left',
                      }}
                    >
                      Sync appears to have stalled (no progress for several minutes).
                      {' '}
                      The sync service may need attention — try again or contact support.
                    </Tag>
                  ) : (
                    <Progress percent={syncPercentage} status={effectiveIsSyncing ? 'active' : 'normal'} />
                  )}
                </div>
              ) : (
                <>
                  {lastSyncSuccess !== null ? (
                    <>
                      {lastSyncSuccess ? (
                        <>
                          {totalSkusWithDiscrepancies === 0 ? (
                            <Tag
                              icon={<CheckCircleOutlined />}
                              color="green"
                              style={{
                                marginBottom: 8,
                                transform: 'scale(1.25)',
                                transformOrigin: 'left',
                              }}
                            >
                              All SKUs are in sync!
                            </Tag>
                          ) : (
                            <Tag
                              icon={<ExclamationCircleOutlined />}
                              color="orange"
                              style={{
                                marginBottom: 8,
                                transform: 'scale(1.25)',
                                transformOrigin: 'left',
                              }}
                            >
                              {`There ${totalSkusWithDiscrepancies === 1 ? 'is' : 'are'} ${pluralize('SKUs', totalSkusWithDiscrepancies, true)} with discrepancies to review.`}
                            </Tag>
                          )}
                        </>
                      ) : (
                        <Tag
                          icon={<ExclamationCircleOutlined />}
                          color="red"
                          style={{
                            marginBottom: 8,
                            transform: 'scale(1.25)',
                            transformOrigin: 'left',
                          }}
                        >
                          The last sync failed.
                        </Tag>
                      )}
                    </>
                  ) : null}
                  <Paragraph style={{ opacity: 0.7, fontStyle: 'italic' }}>Manual sync is not currently running, click the button below to perform a manual sync/audit.</Paragraph>
                </>
              )}
            </>
          )}
        </div>

        <Title level={5}>SKUs</Title>
        <CustomTable
          id="sku-sync-statuses"
          pluralLabel="SKUs"
          dataSource={tableData}
          searchableColumns={['skuName', 'parentProductName', 'matrixSkuId', 'mondaySkuId']}
          controls={(
            <Button
              type="primary"
              onClick={startManualSkuSync}
              loading={effectiveIsSyncing || isStartingManualSkuSync}
              disabled={!dataIsReady}
            >
              {effectiveIsSyncing ? 'Syncing SKUs...' : 'Perform manual sync'}
            </Button>
          )}
        >
          <Column
            title="SKU Name"
            key="skuName"
            dataIndex="skuName"
          />
          <Column
            title="Parent Product"
            key="parentProductName"
            dataIndex="parentProductName"
          />
          <Column
            title="Matrix SKU ID"
            key="matrixSkuId"
            dataIndex="matrixSkuId"
          />
          <Column
            title="Monday SKU ID"
            key="mondaySkuId"
            dataIndex="mondaySkuId"
          />
          <Column
            title="Last checked"
            key="lastChecked"
            dataIndex="lastChecked"
          />
          <Column
            title="Last synced"
            key="lastSynced"
            dataIndex="lastSynced"
          />
          <Column
            title="Discrepancies"
            key="discrepancies"
            dataIndex="discrepancies"
            render={(discrepancies) => {
              if (discrepancies === null) {
                return <div className="empty-cell">N/A</div>;
              }
              return discrepancies.length ? (
                <div className="has-discrepancies-label">{discrepancies.length}</div>
              ) : (
                <div className="no-discrepancies-label">None</div>
              );
            }}
          />
          <Column
            title="Actions"
            key="actions"
            render={(rowData) => {
              /**
               * Deconstruct the required properties from the SKU data.
               */
              const {
                skuName,
                discrepancies,
                matrixSkuId,
                mondaySkuId,
                parentMatrixProductId,
                parentProductName,
              } = rowData;

              /**
               * Determine whether there are any discrepancies.
               */
              const hasNoDiscrepancies = !discrepancies || discrepancies.length === 0;

              return (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 10,
                }}
                >
                  <Tooltip title={mondaySkuId === null ? 'This SKU hasn\'t been linked to an item in Monday yet.' : undefined}>
                    <Button
                      type="ghost"
                      disabled={mondaySkuId === null}
                      onClick={() => window.open(`https://garageproject.monday.com/boards/${MONDAY_SKU_DATABASE_BOARD_ID}/pulses/${mondaySkuId}`, '_blank')}
                    >
                      View in Monday
                    </Button>
                  </Tooltip>

                  <Tooltip title={hasNoDiscrepancies ? 'There are no discrepancies to review.' : undefined}>
                    <Button
                      type="primary"
                      disabled={hasNoDiscrepancies}
                      onClick={() => onReviewDiscrepancies({
                        matrixSkuId,
                        mondaySkuId,
                        parentMatrixProductId,
                        parentProductName,
                        skuName,
                        discrepancies,
                      })}
                    >
                      Review discrepancies
                    </Button>
                  </Tooltip>
                </div>
              );
            }}
          />
        </CustomTable>
      </div>
    </>
  );
};

export default SkuSyncStatusTable;
